import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import "./MISInvoiceInbox.css";

const API = `${process.env.REACT_APP_BASE_URL}/api/mis/email`;
const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};
const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
};
const purposeConfig = {
  mis: { label: "MIS", status: "/google/status", connect: "/google/connect", description: "MIS reports, monthly statements, approvals, and related replies." },
  accounts: { label: "Accounts", status: "/invoice/google/status", connect: "/invoice/google/connect", description: "Invoices, bills, payment confirmations, receipts, remittances, and bank-transfer references." },
};

export default function MISInvoiceInbox({ onClose, initialPurpose = "accounts", lockedPurpose = null }) {
  const [purpose, setPurpose] = useState(lockedPurpose || initialPurpose);
  const [messages, setMessages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [connections, setConnections] = useState({
    mis: { loading: true, connected: false, email: "" },
    accounts: { loading: true, connected: false, email: "" },
  });
  const [connecting, setConnecting] = useState(false);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [replying, setReplying] = useState(false);
  const [error, setError] = useState("");
  const activeConfig = purposeConfig[purpose];
  const activeConnection = connections[purpose];

  const loadInbox = useCallback(async (whichPurpose, pageToken, append = false) => {
    if (append) setLoadingMore(true); else setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/inbox/${whichPurpose}`, {
        headers: authHeaders(),
        params: { maxResults: 25, ...(pageToken ? { pageToken } : {}) },
      });
      setMessages((previous) => append && whichPurpose === purpose ? [...previous, ...(data.messages || [])] : (data.messages || []));
      setNextPageToken(data.nextPageToken || null);
    } catch (e) {
      setMessages([]);
      setNextPageToken(null);
      setError(e.response?.data?.message || `Could not load ${purposeConfig[whichPurpose].label} emails.`);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [purpose]);

  const checkConnection = useCallback(async (whichPurpose, shouldLoad = true) => {
    setConnections((previous) => ({ ...previous, [whichPurpose]: { ...previous[whichPurpose], loading: true } }));
    try {
      const { data } = await axios.get(`${API}${purposeConfig[whichPurpose].status}`, { headers: authHeaders() });
      const next = { loading: false, connected: Boolean(data.connected), email: data.email || "" };
      setConnections((previous) => ({ ...previous, [whichPurpose]: next }));
      if (whichPurpose === purpose && shouldLoad) {
        if (next.connected) await loadInbox(whichPurpose);
        else { setMessages([]); setNextPageToken(null); setLoading(false); }
      }
    } catch (e) {
      setConnections((previous) => ({ ...previous, [whichPurpose]: { loading: false, connected: false, email: "" } }));
      if (whichPurpose === purpose) {
        setLoading(false);
        setError(e.response?.data?.message || `Unable to check ${purposeConfig[whichPurpose].label} Gmail connection.`);
      }
    }
  }, [loadInbox, purpose]);

  useEffect(() => {
    checkConnection("mis", false);
    checkConnection("accounts", false);
  }, []); // check each account once when opening the inbox

  useEffect(() => {
    setSelected(null);
    setReplyText("");
    setMessages([]);
    setNextPageToken(null);
    if (activeConnection.connected) loadInbox(purpose);
    else if (!activeConnection.loading) setLoading(false);
    else setLoading(true);
  }, [purpose, activeConnection.connected, activeConnection.loading, loadInbox]);

  const connectAccount = async (whichPurpose) => {
    setError("");
    setConnecting(whichPurpose);
    try {
      const { data } = await axios.get(`${API}${purposeConfig[whichPurpose].connect}`, { headers: authHeaders() });
      if (!data.url) throw new Error("Server did not return a Google authorization URL.");
      window.location.assign(data.url);
    } catch (e) {
      setError(e.response?.data?.message || e.message || `Could not connect ${purposeConfig[whichPurpose].label} Gmail.`);
      setConnecting(false);
    }
  };

  const openMessage = async (item) => {
    setSelected({ ...item, body: item.snippet || "" });
    setReplyText("");
    setDetailLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/inbox/${purpose}/${encodeURIComponent(item.id)}`, { headers: authHeaders() });
      setSelected(data.message);
    } catch (e) {
      setError(e.response?.data?.message || "Could not open this email.");
    } finally {
      setDetailLoading(false);
    }
  };

  const sendReply = async (event) => {
    event.preventDefault();
    if (!selected || !replyText.trim()) return;
    setReplying(true);
    setError("");
    try {
      await axios.post(`${API}/inbox/${purpose}/${encodeURIComponent(selected.id)}/reply`, { message: replyText }, { headers: authHeaders() });
      setReplyText("");
      setError("");
      await loadInbox(purpose);
      setSelected(null);
      window.alert("Reply sent in the existing Gmail conversation.");
    } catch (e) {
      setError(e.response?.data?.message || "Could not send the reply.");
    } finally {
      setReplying(false);
    }
  };

  const switchPurpose = (nextPurpose) => {
    setPurpose(nextPurpose);
    setError("");
    setLoading(true);
  };

  return (
    <div className="mis-inbox-backdrop" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && !replying && onClose?.()}>
      <section className="mis-inbox-dialog" role="dialog" aria-modal="true" aria-labelledby="mis-inbox-title">
        <header className="mis-inbox-header">
          <div>
            <h2 id="mis-inbox-title">{lockedPurpose ? `${purposeConfig[lockedPurpose].label} Inbox` : "Email Inbox"}</h2>
            <p>Connect two Gmail accounts for separate purposes. Replies are sent from the account whose inbox you are viewing.</p>
          </div>
          <button type="button" className="mis-inbox-close" onClick={onClose} aria-label="Close">×</button>
        </header>

        {!lockedPurpose && <nav className="mis-inbox-tabs" aria-label="Choose email account">
          {Object.entries(purposeConfig).map(([key, config]) => (
            <button key={key} type="button" className={`mis-inbox-tab ${purpose === key ? "is-active" : ""}`} onClick={() => switchPurpose(key)}>
              {config.label}
              <span className={`mis-inbox-tab-status ${connections[key].connected ? "is-connected" : ""}`}>{connections[key].loading ? "Checking" : connections[key].connected ? "Connected" : "Not connected"}</span>
            </button>
          ))}
        </nav>}

        <div className="mis-inbox-account-connections">
          {Object.entries(purposeConfig).filter(([key]) => !lockedPurpose || key === lockedPurpose).map(([key, config]) => {
            const account = connections[key];
            return (
              <section className={`mis-inbox-account-card ${purpose === key ? "is-selected" : ""}`} key={key}>
                <div className="mis-inbox-account-card-heading">
                  <strong>{config.label} Gmail</strong>
                  <span className={`mis-inbox-account-state ${account.connected ? "is-connected" : ""}`}>{account.loading ? "Checking…" : account.connected ? "Connected" : "Not connected"}</span>
                </div>
                <small>{account.email || config.description}</small>
                <button type="button" onClick={() => connectAccount(key)} disabled={Boolean(connecting) || account.loading}>
                  {connecting === key ? "Opening Google…" : account.connected ? `Reconnect ${config.label}` : `Connect ${config.label} email`}
                </button>
              </section>
            );
          })}
        </div>
        <div className="mis-inbox-active-description">Showing <strong>{activeConfig.label}</strong> emails: {activeConfig.description}</div>

        {selected ? (
          <div className="mis-inbox-detail-wrap">
            <button type="button" className="mis-inbox-back" onClick={() => { setSelected(null); setReplyText(""); setError(""); }}>← Back to results</button>
            <article className="mis-inbox-detail">
              <h3>{selected.subject || "(No subject)"}</h3>
              <dl>
                <dt>From</dt><dd>{selected.from || "Unknown sender"}</dd>
                <dt>To</dt><dd>{selected.to || ""}</dd>
                {selected.cc ? <><dt>CC</dt><dd>{selected.cc}</dd></> : null}
                <dt>Received</dt><dd>{formatDate(selected.date)}</dd>
                <dt>Mailbox</dt><dd>{selected.mailbox || activeConnection.email}</dd>
              </dl>
              {detailLoading ? <p className="mis-inbox-muted">Loading email body…</p> : <pre className="mis-inbox-body">{selected.body || selected.snippet || "No readable text body was found in this email."}</pre>}
            </article>
            {!detailLoading && (
              <form className="mis-inbox-reply" onSubmit={sendReply}>
                <label htmlFor="mis-inbox-reply-text">Reply in this conversation</label>
                <textarea id="mis-inbox-reply-text" rows={5} maxLength={20000} required value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="Write your reply…" />
                <div className="mis-inbox-reply-actions">
                  <small>Sent from {activeConfig.label} Gmail and kept in the original thread.</small>
                  <button type="submit" disabled={replying || !replyText.trim()}>{replying ? "Sending reply…" : "Send reply"}</button>
                </div>
              </form>
            )}
          </div>
        ) : (
          <div className="mis-inbox-list-wrap">
            {activeConnection.connected && <div className="mis-inbox-toolbar"><span>{loading ? "Searching Gmail…" : `${messages.length} email${messages.length === 1 ? "" : "s"} loaded`}</span><button type="button" onClick={() => loadInbox(purpose)} disabled={loading || loadingMore}>Refresh</button></div>}
            {!activeConnection.loading && !activeConnection.connected ? <div className="mis-inbox-empty">Connect the {activeConfig.label} Gmail account above to search its inbox.</div> : loading ? <div className="mis-inbox-empty">Loading matching emails…</div> : messages.length === 0 ? <div className="mis-inbox-empty">No matching emails were found. Try Refresh, or check whether the email is archived or in Spam.</div> : (
              <div className="mis-inbox-list">
                {messages.map((item) => (
                  <button type="button" className={`mis-inbox-item ${item.unread ? "is-unread" : ""}`} key={item.id} onClick={() => openMessage(item)}>
                    <span className="mis-inbox-item-top"><strong>{item.from || "Unknown sender"}</strong><time>{formatDate(item.date)}</time></span>
                    <strong className="mis-inbox-subject">{item.subject}</strong>
                    <span className="mis-inbox-snippet">{item.snippet}</span>
                  </button>
                ))}
              </div>
            )}
            {activeConnection.connected && !loading && nextPageToken && <button className="mis-inbox-load-more" type="button" disabled={loadingMore} onClick={() => loadInbox(purpose, nextPageToken, true)}>{loadingMore ? "Loading…" : "Load more emails"}</button>}
          </div>
        )}
        {error && <div className="mis-inbox-error" role="alert">{error}</div>}
        <footer className="mis-inbox-footer"><span>Opening a message does not mark it as read. Replies are sent only after you click Send reply.</span><button type="button" onClick={onClose}>Close</button></footer>
      </section>
    </div>
  );
}
